const CLIENT_NAV_HISTORY_KEY = 'standard-client-navigation-v1';
const MAX_CLIENT_HISTORY = 40;

function normalizeLocation(location: string): string | null {
  if (!location.startsWith('/')) return null;
  const [withoutHash] = location.split('#', 1);
  return withoutHash || '/';
}

function pathnameOf(location: string): string {
  return location.split(/[?#]/, 1)[0] || '/';
}

export function isClientShellLocation(location: string): boolean {
  const pathname = pathnameOf(location);
  return pathname === '/client'
    || pathname.startsWith('/client/')
    || /^\/community\/groups\/[^/]+(?:\/settings)?\/?$/.test(pathname);
}

export function shouldShowClientShellBack(location: string): boolean {
  const pathname = pathnameOf(location);
  if (!isClientShellLocation(pathname)) return false;
  if (
    pathname === '/client'
    || pathname === '/client/plan'
    || pathname === '/client/comms'
    || pathname.startsWith('/client/onboarding')
  ) return false;
  return true;
}

export function updateClientRouteStack(stack: readonly string[], location: string): string[] {
  const normalized = normalizeLocation(location);
  const validStack = stack.filter((entry) => normalizeLocation(entry) && isClientShellLocation(entry));
  if (!normalized || !isClientShellLocation(normalized)) return validStack;

  const last = validStack.at(-1);
  if (last === normalized) return validStack;

  // A browser swipe/back lands on the entry directly below the current one.
  // Collapse the current entry instead of adding the old screen again.
  if (validStack.at(-2) === normalized) return validStack.slice(0, -1);

  return [...validStack, normalized].slice(-MAX_CLIENT_HISTORY);
}

export function popClientRouteStack(
  stack: readonly string[],
  currentLocation: string,
): { stack: string[]; target: string | null } {
  const currentStack = updateClientRouteStack(stack, currentLocation);
  const normalized = normalizeLocation(currentLocation);
  if (!normalized || currentStack.at(-1) !== normalized || currentStack.length < 2) {
    return { stack: currentStack, target: null };
  }

  const nextStack = currentStack.slice(0, -1);
  return { stack: nextStack, target: nextStack.at(-1) ?? null };
}

function readClientRouteStack(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(CLIENT_NAV_HISTORY_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

function writeClientRouteStack(stack: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(CLIENT_NAV_HISTORY_KEY, JSON.stringify(stack));
  } catch {
    // Private browsing and locked-down webviews can deny session storage.
  }
}

export function recordClientLocation(location: string): void {
  writeClientRouteStack(updateClientRouteStack(readClientRouteStack(), location));
}

export function consumeClientBackTarget(location: string): string | null {
  const result = popClientRouteStack(readClientRouteStack(), location);
  writeClientRouteStack(result.stack);
  return result.target;
}
